import {
  SIDEBAR_CATEGORIES,
  isManualMcpPalettePayload,
  workflowBuilderRegistryFromPayload,
  type SidebarCategoryId,
  type SidebarDragPayload,
  type WorkflowRegistryFields,
} from './Sidebar/sidebarPalette';

export type LlmOperation = 'Analyze' | 'Summarize' | 'Generate' | 'Classify' | 'Extract';

export type McpStepKind = 'database' | 'knowledge_base' | 'api' | 'event';

export type McpDatabaseConfig = {
  table: string;
  query: string;
  operation: 'read' | 'write';
};

export type McpKnowledgeBaseConfig = {
  kbName: string;
  queryTemplate: string;
};

export type McpApiConfig = {
  endpointRef: string;
};

export type McpEventConfig = {
  eventName: string;
  payloadTemplate: string;
};

export type McpStep = {
  id: string;
  kind: McpStepKind;
  /** Short label for chip (e.g. "Customers") */
  label: string;
  /** Slug for {{ nodeName.key }} */
  outputKey: string;
  paletteType: string;
  database?: McpDatabaseConfig;
  knowledgeBase?: McpKnowledgeBaseConfig;
  api?: McpApiConfig;
  event?: McpEventConfig;
  /** Manual data-model palette row — editable input/output templates (default `{{default}}`). */
  manualInputTemplate?: string;
  manualOutputTemplate?: string;
} & WorkflowRegistryFields;

export type LlmNodeSettings = {
  nodeName: string;
  operation: LlmOperation;
  parentPrompt: string;
  mcpSteps: McpStep[];
  /** Extra `{{ nodeName.<key> }}` outputs beyond MCP steps (slug keys). */
  extraOutputKeys?: string[];
  /** Verbatim multiline text from the Output variables field (preserves e.g. `{{forecast}}` on reopen). */
  extraOutputsText?: string;
  temperature: number;
  maxTokens: number;
};

/** Serializable LLM node data (no React callbacks). */
export type FlowLlmNodePersistedData = {
  label: string;
  paletteType: string;
} & LlmNodeSettings &
  WorkflowRegistryFields;

const MCP_SOFT_LIMIT = 10;

export function isLlmCategory(category: SidebarCategoryId): boolean {
  return category === SIDEBAR_CATEGORIES.LLMS;
}

/** LLM node placed from the sidebar LLMs category (see `workflowBuilderRegistryFromPayload`). */
export function isLlmsSidebarFlowNode(data: Pick<FlowLlmNodePersistedData, 'workflowCategoryid'>): boolean {
  return data.workflowCategoryid === SIDEBAR_CATEGORIES.LLMS;
}

export function isMcpPaletteCategory(category: SidebarCategoryId): boolean {
  return (
    category === SIDEBAR_CATEGORIES.DATA_MODELS_MCPS ||
    category === SIDEBAR_CATEGORIES.KNOWLEDGE_BASE
  );
}

export function slugifyNodeName(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return s || 'llm_node';
}

export function shortLabelFromPalette(label: string): string {
  return label.replace(/^CRM\s*-\s*/i, '').replace(/^Sales DB\s*-\s*/i, '').replace(/^GDrive\s*-\s*/i, '').trim() || label;
}

export function makeOutputKey(base: string, existing: Set<string>): string {
  let k = slugifyNodeName(base).replace(/^_+|_+$/g, '') || 'step';
  if (!existing.has(k)) return k;
  let i = 2;
  while (existing.has(`${k}_${i}`)) i += 1;
  return `${k}_${i}`;
}

export function createMcpStepFromPalette(payload: SidebarDragPayload, existingKeys: Set<string>): McpStep | null {
  const id = `mcp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const short = shortLabelFromPalette(payload.label);
  const paletteType = payload.type;
  const reg = workflowBuilderRegistryFromPayload(payload);

  if (payload.category === SIDEBAR_CATEGORIES.KNOWLEDGE_BASE) {
    const outputKey = makeOutputKey(short || 'kb', existingKeys);
    return {
      id,
      kind: 'knowledge_base',
      label: short || payload.label,
      outputKey,
      paletteType,
      knowledgeBase: {
        kbName: slugifyNodeName(short || payload.label) || 'kb',
        queryTemplate: '{{input}}',
      },
      ...reg,
    };
  }

  if (isManualMcpPalettePayload(payload)) {
    const outputKey = makeOutputKey('default', existingKeys);
    return {
      id,
      kind: 'database',
      label: short || payload.label,
      outputKey,
      paletteType,
      manualInputTemplate: '{{default}}',
      manualOutputTemplate: '{{default}}',
      database: {
        table: slugifyNodeName(short || payload.label) || 'manual',
        query: '{{default}}',
        operation: 'read',
      },
      ...reg,
    };
  }

  if (paletteType === 'MCP_EventEmit' || paletteType.includes('EventEmit')) {
    const outputKey = makeOutputKey('event', existingKeys);
    return {
      id,
      kind: 'event',
      label: short || 'Event',
      outputKey,
      paletteType,
      event: {
        eventName: 'lead.enriched',
        payloadTemplate: '{\n  "data": {{llm.output}}\n}',
      },
      ...reg,
    };
  }

  if (paletteType === 'MCP_GDrive_Documents' || paletteType.includes('GDrive')) {
    const outputKey = makeOutputKey('documents', existingKeys);
    return {
      id,
      kind: 'knowledge_base',
      label: short || 'KB',
      outputKey,
      paletteType,
      knowledgeBase: {
        kbName: 'documents',
        queryTemplate: '{{input}}',
      },
      ...reg,
    };
  }

  if (paletteType.includes('Http') || paletteType.includes('API')) {
    const outputKey = makeOutputKey('api', existingKeys);
    return {
      id,
      kind: 'api',
      label: short,
      outputKey,
      paletteType,
      api: { endpointRef: '' },
      ...reg,
    };
  }

  // Default: database-style MCP from DATA MODELS
  const outputKey = makeOutputKey(short, existingKeys);
  const tableGuess = slugifyNodeName(short).replace(/_/g, '_') || 'records';
  return {
    id,
    kind: 'database',
    label: short,
    outputKey,
    paletteType,
    database: {
      table: tableGuess,
      query: '',
      operation: 'read',
    },
    ...reg,
  };
}

export function defaultLlmSettings(modelLabel: string, paletteType: string): LlmNodeSettings {
  const nodeName = slugifyNodeName(modelLabel.replace(/\s+/g, '_'));
  return {
    nodeName,
    operation: 'Analyze',
    parentPrompt: `You orchestrate tools to fulfill the request.\n{{input}}\n`,
    mcpSteps: [],
    extraOutputKeys: [],
    temperature: 0.3,
    maxTokens: 2000,
  };
}

/** LLM node + one Manual MCP step; parent prompt and step templates default to `{{default}}`. */
export function createLlmNodeDataFromManualMcpDrop(payload: SidebarDragPayload): FlowLlmNodePersistedData {
  const keys = new Set<string>();
  const step = createMcpStepFromPalette(payload, keys);
  const nodeName = slugifyNodeName(payload.label);
  const reg = workflowBuilderRegistryFromPayload(payload);
  if (!step) {
    return {
      label: payload.label,
      paletteType: payload.type,
      ...defaultLlmSettings(payload.label, payload.type),
      parentPrompt: '{{default}}',
      ...reg,
    };
  }
  return {
    label: payload.label,
    paletteType: payload.type,
    nodeName,
    operation: 'Analyze',
    parentPrompt: '{{default}}',
    mcpSteps: [step],
    extraOutputKeys: [],
    temperature: 0.3,
    maxTokens: 2000,
    ...reg,
  };
}

export function operationPromptHint(operation: LlmOperation): string {
  switch (operation) {
    case 'Analyze':
      return 'Analyze the following in depth:\n{{input}}\n';
    case 'Summarize':
      return 'Summarize clearly and concisely:\n{{input}}\n';
    case 'Generate':
      return 'Generate content based on:\n{{input}}\n';
    case 'Classify':
      return 'Classify and label the following:\n{{input}}\n';
    case 'Extract':
      return 'Extract structured fields from:\n{{input}}\n';
    default:
      return '{{input}}\n';
  }
}

export function buildLlmOutputLines(nodeName: string, steps: McpStep[], extraOutputKeys?: string[] | null): string {
  const lines: string[] = [`{{ ${nodeName}.output }}`];
  const used = new Set<string>(['output']);
  for (const s of steps) {
    if (!used.has(s.outputKey)) {
      used.add(s.outputKey);
      lines.push(`{{ ${nodeName}.${s.outputKey} }}`);
    }
  }
  if (steps.some((s) => s.kind === 'event')) {
    if (!used.has('event_emitted')) {
      used.add('event_emitted');
      lines.push(`{{ ${nodeName}.event_emitted }}`);
    }
  }
  for (const raw of extraOutputKeys ?? []) {
    const k = slugifyNodeName(raw).replace(/^_+|_+$/g, '') || 'extra';
    if (used.has(k)) continue;
    used.add(k);
    lines.push(`{{ ${nodeName}.${k} }}`);
  }
  return lines.join('\n');
}

export function truncatePrompt(text: string, maxLen = 72): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

export function createBlankDatabaseStep(existingKeys: Set<string>): McpStep {
  const id = `mcp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    id,
    kind: 'database',
    label: 'New MCP',
    outputKey: makeOutputKey('step', existingKeys),
    paletteType: 'MCP_Custom',
    database: { table: '', query: '', operation: 'read' },
  };
}

export { MCP_SOFT_LIMIT };
